import { inject, Injectable } from "@angular/core";
import {
  IAppPlugin,
  IAppPluginWidget,
  IModuleWidget,
  IPermissionsFlags,
  IPluginPermissions,
  IUser,
  ModuleWidgetRegistry,
  PluginInjectorService,
  PluginInjectorWidget,
} from "core-client/api";

@Injectable({ providedIn: "root" })
export class DashboardWidgetLoader {
  private readonly moduleWidgetRegistry = inject(ModuleWidgetRegistry);
  private readonly pluginInjectorService = inject(PluginInjectorService);

  buildWidgets(
    appPluginWidgets: IAppPluginWidget[],
    appPlugins: IAppPlugin[],
    user: IUser | undefined,
    permissions: IPluginPermissions[],
  ): PluginInjectorWidget.IWidget[] {
    const pluginIdByAppPlugin = new Map<string, string>(
      appPlugins.map((appPlugin: IAppPlugin) => [appPlugin._id, appPlugin.plugin]),
    );
    const moduleWidgets = this.moduleWidgetRegistry.getModuleWidgets();
    const widgets: PluginInjectorWidget.IWidget[] = [];

    for (const appPluginWidget of appPluginWidgets) {
      if (user) {
        this.pluginInjectorService.applyExtraData(appPluginWidget, user);
      }
      const widget: PluginInjectorWidget.IWidget = {
        _id: appPluginWidget._id,
        name: appPluginWidget.name,
        x: appPluginWidget.layout.position_x,
        y: appPluginWidget.layout.position_y,
        rows: appPluginWidget.layout.rows,
        cols: appPluginWidget.layout.columns,
        height: appPluginWidget.layout.height_px,
        width: appPluginWidget.layout.width_px,
        pluginId: pluginIdByAppPlugin.get(appPluginWidget.app_plugin) ?? "",
        pluginModule: String(appPluginWidget.plugin_module),
        appPluginWidget,
        isHeaderHovered: false,
      };
      const moduleWidget = moduleWidgets.find(
        (candidate: IModuleWidget) =>
          candidate.pluginId === widget.pluginId &&
          candidate.pluginModule === widget.pluginModule,
      );
      if (moduleWidget) {
        widget.widgetPermissions = moduleWidget.widgetPermissions;
      }
      if (this.isWidgetEnabled(widget, permissions)) {
        widgets.push(widget);
      }
    }
    return widgets;
  }

  // The grid checks collisions only when a widget is added, so a placed widget must keep its object.
  keepPlaced(
    current: PluginInjectorWidget.IWidget[],
    built: PluginInjectorWidget.IWidget[],
  ): PluginInjectorWidget.IWidget[] {
    const placed = new Map(current.map((widget: PluginInjectorWidget.IWidget) => [widget._id, widget]));
    return built.map((widget: PluginInjectorWidget.IWidget) => {
      const kept = placed.get(widget._id);
      if (!kept) return widget;
      const { x, y, cols, rows, height, width, ...refreshed } = widget;
      return Object.assign(kept, refreshed);
    });
  }

  isWidgetEnabled(
    widget: PluginInjectorWidget.IWidget,
    permissions: IPluginPermissions[],
  ): boolean {
    const pluginEnabled =
      this.pluginInjectorService.getPluginById(widget.pluginId)?.enabled ?? false;
    return (
      pluginEnabled &&
      this.widgetEnabled(widget.pluginId, widget.pluginModule, permissions, widget.widgetPermissions)
    );
  }

  private widgetEnabled(
    pluginId: string,
    pluginModule: string,
    permissions: IPluginPermissions[],
    widgetPermissions?: PluginInjectorWidget.IWidgetPermissions,
  ): boolean {
    const permissionRef = widgetPermissions?.widgetEnabledPermission?.permissionRef;
    if (!permissionRef) {
      return false;
    }
    const widgetFlags = permissions.find(
      (pluginPermission: IPluginPermissions) => pluginPermission.plugin_id === pluginId,
    )?.widget_permissions;
    if (!widgetFlags) {
      return false;
    }
    const hasModuleWidgetPermissions = Object.values(widgetFlags).every(
      (flag: IPermissionsFlags | boolean) => typeof flag !== "boolean",
    );
    if (!hasModuleWidgetPermissions && widgetFlags[permissionRef]) {
      return widgetFlags[permissionRef] as boolean;
    }
    const moduleFlags = widgetFlags[pluginModule.toLowerCase()];
    if (pluginModule && moduleFlags) {
      return (moduleFlags as IPermissionsFlags)[permissionRef];
    }
    return false;
  }
}
